/**
 * EVAR (Endovascular Aortic Repair) sizing template.
 *
 * Defines the standard P1-P8 keypoints and all measurements
 * for EVAR pre-operative planning.
 */

import type { IReportSnapshotTemplate } from '@/components/report';
import { ViewMode } from '@/visualization/display/types';
import {
    type ISizingTemplate,
    MeasurementType,
    MeasurementCategory,
} from '@/visualization/domain/sizing/templates/types';

export const EVAR_TEMPLATE: ISizingTemplate = {
    id: 'evar-v1',
    name: 'EVAR Sizing',

    keypoints: [
        { id: 'P1', name: 'Proximal End', shortLabel: 'P1', editable: false },
        { id: 'P2', name: 'Lowest Renal Artery', shortLabel: 'P2', editable: true },
        { id: 'P3', name: 'Bottom Infrarenal Neck', shortLabel: 'P3', editable: true },
        { id: 'P4', name: 'Aortic Bifurcation', shortLabel: 'P4', editable: true, ghostOnOtherLegs: true },
        { id: 'P5', name: 'Right Iliac Bifurcation', shortLabel: 'P5', editable: true },
        { id: 'P6', name: 'Left Iliac Bifurcation', shortLabel: 'P6', editable: true },
        { id: 'P7', name: 'Right Distal End', shortLabel: 'P7', editable: false },
        { id: 'P8', name: 'Left Distal End', shortLabel: 'P8', editable: false },
    ],

    measurements: [
        // ── Lengths ──
        {
            type: MeasurementType.LENGTH,
            id: 'neck-length',
            name: 'Aortic Neck [P2-P3]',
            shortLabel: 'L[P2-P3]',
            category: MeasurementCategory.LENGTH,
            fromKeypointId: 'P2',
            toKeypointId: 'P3',
        },
        {
            type: MeasurementType.LENGTH,
            id: 'aortic-trunk-length',
            name: 'Lowest Renal to Aortic Bifurcation [P2-P4]',
            shortLabel: 'L[P2-P4]',
            category: MeasurementCategory.LENGTH,
            fromKeypointId: 'P2',
            toKeypointId: 'P4',
        },
        {
            type: MeasurementType.LENGTH,
            id: 'right-iliac-length',
            name: 'Right Common Iliac [P4-P5]',
            shortLabel: 'L[P4-P5]',
            category: MeasurementCategory.LENGTH,
            fromKeypointId: 'P4',
            toKeypointId: 'P5',
        },
        {
            type: MeasurementType.LENGTH,
            id: 'left-iliac-length',
            name: 'Left Common Iliac [P4-P6]',
            shortLabel: 'L[P4-P6]',
            category: MeasurementCategory.LENGTH,
            fromKeypointId: 'P4',
            toKeypointId: 'P6',
        },
        {
            type: MeasurementType.LENGTH,
            id: 'right-leg-length',
            name: 'Lowest Renal to Right Internal Iliac [P2-P5]',
            shortLabel: 'L[P2-P5]',
            category: MeasurementCategory.LENGTH,
            fromKeypointId: 'P2',
            toKeypointId: 'P5',
        },
        {
            type: MeasurementType.LENGTH,
            id: 'left-leg-length',
            name: 'Lowest Renal to Left Internal Iliac [P2-P6]',
            shortLabel: 'L[P2-P6]',
            category: MeasurementCategory.LENGTH,
            fromKeypointId: 'P2',
            toKeypointId: 'P6',
        },

        // ── Diameters at keypoints ──
        {
            type: MeasurementType.DIAMETER,
            id: 'dP2',
            name: 'P2',
            shortLabel: 'P2',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P2',
        },
        {
            type: MeasurementType.DIAMETER,
            id: 'dP3',
            name: 'P3',
            shortLabel: 'P3',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P3',
        },
        {
            type: MeasurementType.DIAMETER,
            id: 'dP4',
            name: 'P4',
            shortLabel: 'P4',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P4',
        },
        {
            type: MeasurementType.DIAMETER,
            id: 'dP5',
            name: 'P5',
            shortLabel: 'P5',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P5',
        },
        {
            type: MeasurementType.DIAMETER,
            id: 'dP6',
            name: 'P6',
            shortLabel: 'P6',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P6',
        },

        // ── Offset diameters ──
        {
            type: MeasurementType.OFFSET_DIAMETER,
            id: 'dP2+10',
            name: 'D P2 +10',
            shortLabel: 'P2+10',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P2',
            offsetMm: 10,
            isDefault: true,
        },
        {
            type: MeasurementType.OFFSET_DIAMETER,
            id: 'dP2+15',
            name: 'D P2 +15',
            shortLabel: 'P2+15',
            category: MeasurementCategory.DIAMETER,
            keypointId: 'P2',
            offsetMm: 15,
            isDefault: true,
        },

        // ── Min/Max diameters ──
        {
            type: MeasurementType.MIN_MAX_DIAMETER,
            id: 'max-aneurysm-d',
            name: 'Max Aneurysm',
            shortLabel: 'Max Aneurysm',
            category: MeasurementCategory.DIAMETER,
            fromKeypointId: 'P2',
            toKeypointId: 'P4',
            mode: 'max',
        },
        {
            type: MeasurementType.MIN_MAX_DIAMETER,
            id: 'min-right-iliac-d',
            name: 'Min Right Iliac',
            shortLabel: 'Min Right Iliac',
            category: MeasurementCategory.DIAMETER,
            fromKeypointId: 'P5',
            toKeypointId: 'P7',
            mode: 'min',
        },
        {
            type: MeasurementType.MIN_MAX_DIAMETER,
            id: 'min-left-iliac-d',
            name: 'Min Left Iliac',
            shortLabel: 'Min Left Iliac',
            category: MeasurementCategory.DIAMETER,
            fromKeypointId: 'P6',
            toKeypointId: 'P8',
            mode: 'min',
        },

        // ── Angles ──
        {
            type: MeasurementType.ANGLE,
            id: 'suprarenal-angle',
            name: 'Suprarenal angle (P1, P2, P3)',
            shortLabel: 'Suprarenal angle',
            category: MeasurementCategory.ANGLE,
            keypointIds: ['P1', 'P2', 'P3'],
            formula: 'supplementary',
        },
        {
            type: MeasurementType.ANGLE,
            id: 'infrarenal-angle',
            name: 'Infrarenal Angle (P2, P3, P4)',
            shortLabel: 'Infrarenal angle',
            category: MeasurementCategory.ANGLE,
            keypointIds: ['P2', 'P3', 'P4'],
            formula: 'supplementary',
        },

        // ── Derived ──
        {
            type: MeasurementType.DERIVED,
            id: 'neck-taper',
            name: 'Neck Conicity',
            shortLabel: 'Neck Conicity',
            category: MeasurementCategory.DERIVED,
            dependencies: ['dP2', 'dP3'],
            compute: (deps) => ((deps['dP2'] - deps['dP3']) / deps['dP2']) * 100,
        },
    ],
};

/**
 * Defines the captures to include on the snapshot page of the EVAR report.
 *
 * Measurement IDs (dP2–dP4) correspond to the diameter measurements defined in
 * EVAR_TEMPLATE above. Oblique captures use a standalone centerline-view viewport
 * that automatically wires up sizing overlays (contour + diameter line) via the
 * display feature system.
 *
 * To add or remove captures: edit this constant only - no other file needs changing.
 */
export const EVAR_SNAPSHOT_TEMPLATE: IReportSnapshotTemplate = {
    id: 'evar-report',
    configs: [
        {
            id: 'slice-dP2',
            label: 'Diameter P2 (lower renal arteries)',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP2',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-dP3',
            label: 'Diameter P3 (lower infrarenal neck)',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP3',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-dP4',
            label: 'Diameter P4 (aortic bifurcation)',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP4',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-dP5',
            label: 'Diameter P5 (right iliac bifurcation)',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP5',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-dP6',
            label: 'Diameter P6 (left iliac bifurcation)',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP6',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-dP2+10',
            label: 'Diameter P2 +10 mm',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP2+10',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-dP2+15',
            label: 'Diameter P2 +15 mm',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'dP2+15',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-max-aneurysm-d',
            label: 'Maximum aneurysm diameter',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'max-aneurysm-d',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-min-right-iliac-d',
            label: 'Minimum right iliac diameter',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'min-right-iliac-d',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
        {
            id: 'slice-min-left-iliac-d',
            label: 'Minimum left iliac diameter',
            viewMode: ViewMode.VIEW_2D_OBLIQUE,
            measurementId: 'min-left-iliac-d',
            fieldOfViewMm: 'auto',
            size: { width: 400, height: 400 },
        },
    ],
};
